import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { StorageContext } from '../contexts/StorageContext';
import { ProjectContext } from '../contexts/ProjectContext';
import { getFileIcon, getFileName, createDirectoryTree, sortDirectoryTree } from '../utils/fileUtils';
import LoadingSpinner from './LoadingSpinner';
import './FileExplorer.css';

/**
 * File explorer component to display project files
 * @param {Object} props - Component props
 * @param {boolean} props.treeView - Whether to display files as tree view
 */
function FileExplorer({ treeView = true }) {
  const { storage } = useContext(StorageContext);
  const { project } = useContext(ProjectContext);
  const { fileId } = useParams();
  const [files, setFiles] = useState([]);
  const [components, setComponents] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [directoryTree, setDirectoryTree] = useState(null);
  const navigate = useNavigate();
  
  // Load files and components
  useEffect(() => {
    const loadData = async () => {
      if (!project) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load files and components
        const projectFiles = await storage.getFilesByProject(project.id);
        const projectComponents = await storage.getComponentsByProject(project.id);
        
        setFiles(projectFiles);
        setComponents(projectComponents);
        
        // Initialize expanded state
        if (treeView) {
          // Create directory tree
          const tree = createDirectoryTree(projectFiles);
          setDirectoryTree(tree);
          
          // Expand first-level directories by default
          const initialExpanded = {};
          Object.keys(tree.children).forEach(key => {
            if (tree.children[key].type === 'directory') {
              initialExpanded[key] = true;
            }
          });
          setExpandedItems(initialExpanded);
        } else {
          // Initialize expanded state for components
          const expandedState = {};
          projectComponents.forEach(comp => {
            expandedState[comp.id] = false;
          });
          setExpandedItems(expandedState);
        }
      } catch (err) {
        console.error('Error loading file explorer data:', err);
        setError('Failed to load files');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [project, storage, treeView]);
  
  // Toggle expanded state
  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Render directory tree view
  const renderTreeView = (items, parentPath = '') => {
    return (
      <ul className="directory-list">
        {items.map(item => {
          const itemPath = parentPath ? `${parentPath}/${item.name}` : item.name;
          
          if (item.type === 'directory') {
            const isExpanded = expandedItems[itemPath] || false;
            const hasChildren = item.children && Object.keys(item.children).length > 0;
            
            return (
              <li key={itemPath} className="directory-item">
                <div 
                  className={`directory-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleExpanded(itemPath)}
                >
                  <span className="directory-icon">
                    {isExpanded ? '📂' : '📁'}
                  </span>
                  <span className="directory-name">{item.name}</span>
                  {hasChildren && (
                    <span className="directory-toggle">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  )}
                </div>
                
                {isExpanded && hasChildren && (
                  renderTreeView(sortDirectoryTree(item.children), itemPath)
                )}
              </li>
            );
          } else {
            // File item
            return (
              <li 
                key={itemPath} 
                className={`file-item ${item.id === fileId ? 'active' : ''}`}
              >
                <Link 
                  to={`/project/${project.id}/file/${item.id}`} 
                  className="file-link"
                >
                  <span className="file-icon">
                    {getFileIcon(item.language)}
                  </span>
                  <span className="file-name">{item.name}</span>
                </Link>
              </li>
            );
          }
        })}
      </ul>
    );
  };
  
  // Render component-based view
  const renderComponentView = () => {
    // Group files by component
    const filesByComponent = components.reduce((acc, component) => {
      acc[component.id] = files.filter(f => f.componentId === component.id);
      return acc;
    }, {});
    
    // Unorganized files (not in any component)
    const unorganizedFiles = files.filter(f => !f.componentId);
    
    return (
      <>
        <ul className="component-list">
          {components.map(component => (
            <li key={component.id} className="component-item">
              <div
                className={`component-header ${expandedItems[component.id] ? 'expanded' : ''}`}
                onClick={() => toggleExpanded(component.id)}
              >
                <span className="component-name">{component.name}</span>
                <span className="component-count">
                  {filesByComponent[component.id]?.length || 0} files
                </span>
              </div>
              
              {expandedItems[component.id] && filesByComponent[component.id]?.length > 0 && (
                <ul className="file-list">
                  {filesByComponent[component.id].map(file => (
                    <li key={file.id} className={`file-item ${file.id === fileId ? 'active' : ''}`}>
                      <Link to={`/project/${project.id}/file/${file.id}`} className="file-link">
                        <span className="file-icon">{getFileIcon(file.language)}</span>
                        <span className="file-name">{getFileName(file.path)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        
        {unorganizedFiles.length > 0 && (
          <div className="unorganized-files">
            <div className="unorganized-header">Unorganized Files</div>
            <ul className="file-list">
              {unorganizedFiles.map(file => (
                <li key={file.id} className={`file-item ${file.id === fileId ? 'active' : ''}`}>
                  <Link to={`/project/${project.id}/file/${file.id}`} className="file-link">
                    <span className="file-icon">{getFileIcon(file.language)}</span>
                    <span className="file-name">{getFileName(file.path)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };
  
  // Switch between tree view and component view
  const toggleViewMode = () => {
    navigate(`/project/${project.id}/${treeView ? 'components' : 'files'}`);
  };
  
  if (loading) {
    return <div className="file-explorer-loading"><LoadingSpinner size="small" /></div>;
  }
  
  if (error) {
    return <div className="file-explorer-error">{error}</div>;
  }
  
  return (
    <div className="file-explorer">
      <div className="file-explorer-header">
        <h3>Files</h3>
        <button 
          className="view-mode-toggle"
          onClick={toggleViewMode}
          title={treeView ? 'Switch to Component View' : 'Switch to Tree View'}
        >
          {treeView ? '⚙️' : '📁'}
        </button>
      </div>
      
      <div className="file-explorer-content">
        {files.length > 0 ? (
          <>
            {treeView ? (
              directoryTree && renderTreeView(sortDirectoryTree(directoryTree.children))
            ) : (
              renderComponentView()
            )}
          </>
        ) : (
          <div className="no-files">No files created yet.</div>
        )}
      </div>
    </div>
  );
}

FileExplorer.propTypes = {
  treeView: PropTypes.bool
};

export default FileExplorer;
